import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {OverlayTrigger, Tooltip} from "react-bootstrap";

import {MapSectorInfo, MapSectorInfoValue} from "../../../services/models/map_sector";
import {MapSettings, mapSettingsIndicatorDataRFTitles} from "../../../services/models/map_settings";
import {useFetchMapSectorsDataRF} from "../../../hooks/useQuery/useFetchMapSectorsDataRF";
import {CELL_SIZE, GAP, getElementAt, getSectorColorForDataRF, GRID_COLUMNS, isDarkColor, mapGrid} from "./utils";
import styles from "./MapOfSubjectsView.module.css";

interface Props {
    sectors: MapSectorInfo[];
    mapSettingsData: MapSettings;
    isAdministrator: boolean;
    selectedYear: number | null;
}

export const MapSectorsDataRFView = ({sectors, mapSettingsData, isAdministrator, selectedYear}: Props) => {
    const {mapSectorsDataRF} = useFetchMapSectorsDataRF();
    const [sectorsValues, setSectorsValues] = useState<MapSectorInfoValue[]>([]);
    const [avgValue, setAvgValue] = useState(0);

    useEffect(() => {
        if (mapSectorsDataRF && mapSettingsData && sectors?.length) {
            const result: MapSectorInfoValue[] = [];
            
            // Проверяем, нужен ли год для текущего индикатора
            const needsYear = mapSettingsData.indicatorDataRF === 'rateHospitalizationChildrenBeds' ||
                              mapSettingsData.indicatorDataRF === 'rateHospitalizationAdultsBeds';
            
            // Если нужен год, но он не выбран - не показываем данные
            if (needsYear && !selectedYear) {
                setSectorsValues([]);
                setAvgValue(0);
                return;
            }
            
            // Функция для получения числового значения из данных
            const getNumericValue = (value: number | { [key: number]: number }): number => {
                if (typeof value === 'number') {
                    return value;
                }
                // Если это объект и выбран год - берем значение для этого года
                if (selectedYear && value[selectedYear] !== undefined) {
                    return value[selectedYear];
                }
                // Если год не выбран или нет данных для года - возвращаем 0
                return 0;
            };

            const filteredData = mapSectorsDataRF
                .map(({data}) => data[mapSettingsData.indicatorDataRF])
                .filter(Boolean)
                .map(getNumericValue)
                .filter(val => val > 0); // Фильтруем нулевые значения для расчета среднего
            
            const avg = filteredData.length > 0 
                ? filteredData.reduce((a, b) => a + b, 0) / filteredData.length 
                : 0;

            for (const element of sectors) {
                const findedData = mapSectorsDataRF.find(({sectorId}) => sectorId === element.sectorId);

                if (findedData && findedData.data[mapSettingsData.indicatorDataRF]) {
                    const rawValue = findedData.data[mapSettingsData.indicatorDataRF];
                    const numericValue = getNumericValue(rawValue);
                    
                    result.push({
                        sectorId: element.sectorId,
                        value: numericValue.toString(),
                        title: mapSettingsIndicatorDataRFTitles[mapSettingsData.indicatorDataRF],
                    });
                }
            }

            setAvgValue(avg);
            setSectorsValues(result);
        }
    }, [sectors, mapSettingsData, mapSectorsDataRF, selectedYear]);

    const renderTooltip = (tooltip: string) => (props: React.ComponentPropsWithoutRef<typeof Tooltip>) =>
        (
            <Tooltip id="button-tooltip" {...props}>
                {tooltip}
            </Tooltip>
        );

    return (
        <div
            className={styles.wrapper}
            style={{
                width: `${GRID_COLUMNS * (CELL_SIZE + GAP)}px`,
                gridTemplateColumns: `repeat(${GRID_COLUMNS}, ${CELL_SIZE}px)`,
                gap: GAP,
            }}
        >
            {mapGrid.map(({x, y}, index) => {
                const element = getElementAt(sectors, x, y);
                const valueInfo = sectorsValues.find(({sectorId}) => sectorId === element?.sectorId);
                const isValueExists = valueInfo?.value !== "-" && valueInfo?.value !== "";
                const color =
                    valueInfo?.value === undefined
                        ? "#ededed"
                        : isValueExists
                            ? getSectorColorForDataRF(Number(valueInfo.value), avgValue)
                            : "#fff";
                const tooltip = valueInfo && isValueExists ? `${valueInfo.title}: ${valueInfo.value}` : "Нет данных";

                return (
                    <div
                        key={index}
                        className={styles.mapItem}
                        style={{
                            width: CELL_SIZE,
                            height: CELL_SIZE,
                            border: element ? "1px solid #aaa" : "none",
                        }}
                    >
                        {element && (
                            <OverlayTrigger placement="top" delay={{show: 250, hide: 250}}
                                            overlay={renderTooltip(tooltip)}>
                                {isAdministrator ? (
                                    <Link
                                        to={`/administrator/cabinet/sectors/${element.sectorId}`}
                                        className={styles.mapItemLink}
                                        style={{
                                            backgroundColor: element ? color : "transparent",
                                            color: isDarkColor(color) ? "#ddd" : "#222",
                                        }}
                                    >
                                        <strong>{element.shortName}</strong>
                                        {valueInfo?.value === undefined ? (
                                            <span style={{visibility: "hidden"}}>-</span>
                                        ) : (
                                            <span>{valueInfo.value || "-"}</span>
                                        )}
                                    </Link>
                                ) : (
                                    <div
                                        className={styles.mapItemLink}
                                        style={{
                                            backgroundColor: element ? color : "transparent",
                                            color: isDarkColor(color) ? "#ddd" : "#222",
                                        }}
                                    >
                                        <strong>{element.shortName}</strong>
                                        {valueInfo?.value === undefined ? (
                                            <span style={{visibility: "hidden"}}>-</span>
                                        ) : (
                                            <span>{valueInfo.value || "-"}</span>
                                        )}
                                    </div>
                                )}
                            </OverlayTrigger>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
